/**
 * FulfillmentSelector — Pickup vs. Delivery toggle for RTE booking panel.
 *
 * Shows pickup (free) and delivery options with real-time quote.
 * Delivery quote is fetched when the user enters/confirms their zip code.
 *
 * Spec: §22 Delivery Provider Integration, §6 Fulfillment Selection UX
 */
import React, { useState, useCallback } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types } from '../../util/sdkLoader';

import css from './FulfillmentSelector.module.css';

const { Money } = types;

const FulfillmentSelector = props => {
  const {
    listingFulfillment = [],
    selectedMethod,
    onSelectMethod,
    deliveryQuote,
    onFetchDeliveryQuote,
    isLoadingQuote,
    quoteError,
    currency,
    providerDistance,
    rootClassName,
    className,
  } = props;

  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const [deliveryAddress, setDeliveryAddress] = useState({
    street1: '',
    city: '',
    state: '',
    zip: '',
  });
  const savedAddress = typeof window !== 'undefined' ? localStorage.getItem('rte_delivery_address') : null;

  const hasPickup = listingFulfillment.includes('pickup');
  const hasDelivery = listingFulfillment.includes('delivery');

  const handleAddressSubmit = useCallback(
    e => {
      e.preventDefault();
      const { street1, city, state, zip } = deliveryAddress;
      if (street1.trim() && city.trim() && state.trim() && zip.length >= 5 && onFetchDeliveryQuote) {
        onFetchDeliveryQuote(deliveryAddress);
        localStorage.setItem('rte_delivery_address', JSON.stringify(deliveryAddress));
      }
    },
    [deliveryAddress, onFetchDeliveryQuote]
  );

  // Auto-fetch quote if we have a saved address and delivery is selected
  React.useEffect(() => {
    if (savedAddress && selectedMethod === 'delivery' && !deliveryQuote && !isLoadingQuote) {
      try {
        const parsedAddress = JSON.parse(savedAddress);
        setDeliveryAddress(parsedAddress);
        if (onFetchDeliveryQuote) {
          onFetchDeliveryQuote(parsedAddress);
        }
      } catch (e) {
        // Invalid stored address, ignore
      }
    }
  }, [savedAddress, selectedMethod]); // eslint-disable-line

  if (!hasPickup && !hasDelivery) return null;

  return (
    <div className={classes}>
      <h3 className={css.heading}>
        {intl.formatMessage({ id: 'FulfillmentSelector.heading' })}
      </h3>
      <div className={css.options}>
        {hasPickup && (
          <button
            type="button"
            className={classNames(css.option, {
              [css.optionSelected]: selectedMethod === 'pickup',
            })}
            onClick={() => onSelectMethod('pickup')}
          >
            <div className={css.optionIcon}>🚗</div>
            <div className={css.optionContent}>
              <span className={css.optionName}>
                {intl.formatMessage({ id: 'FulfillmentSelector.pickup' })}
              </span>
              <span className={css.optionPrice}>
                {intl.formatMessage({ id: 'FulfillmentSelector.free' })}
              </span>
              <span className={css.optionDescription}>
                {providerDistance
                  ? intl.formatMessage(
                      { id: 'FulfillmentSelector.pickupDistance' },
                      { distance: providerDistance }
                    )
                  : intl.formatMessage({ id: 'FulfillmentSelector.pickupDescription' })}
              </span>
            </div>
            {selectedMethod === 'pickup' && <span className={css.checkmark}>✓</span>}
          </button>
        )}

        {hasDelivery && (
          <button
            type="button"
            className={classNames(css.option, {
              [css.optionSelected]: selectedMethod === 'delivery',
            })}
            onClick={() => onSelectMethod('delivery')}
          >
            <div className={css.optionIcon}>🚚</div>
            <div className={css.optionContent}>
              <span className={css.optionName}>
                {intl.formatMessage({ id: 'FulfillmentSelector.delivery' })}
              </span>
              {deliveryQuote ? (
                <span className={css.optionPrice}>
                  {formatMoney(
                    intl,
                    new Money(deliveryQuote.outbound + deliveryQuote.return, currency)
                  )}
                  <span className={css.priceNote}>
                    {intl.formatMessage({ id: 'FulfillmentSelector.totalBothWays' })}
                  </span>
                </span>
              ) : (
                <span className={css.optionPrice}>
                  {intl.formatMessage({ id: 'FulfillmentSelector.getQuote' })}
                </span>
              )}
              <span className={css.optionDescription}>
                {intl.formatMessage({ id: 'FulfillmentSelector.deliveryDescription' })}
              </span>
            </div>
            {selectedMethod === 'delivery' && <span className={css.checkmark}>✓</span>}
          </button>
        )}
      </div>

      {/* Delivery address entry for delivery quotes */}
      {selectedMethod === 'delivery' && !deliveryQuote && (
        <div className={css.addressForm}>
          <h4 className={css.addressFormTitle}>
            {intl.formatMessage({ id: 'FulfillmentSelector.deliveryAddressTitle' })}
          </h4>
          <form onSubmit={handleAddressSubmit} className={css.addressFields}>
            <input
              type="text"
              className={css.addressInput}
              placeholder={intl.formatMessage({ id: 'FulfillmentSelector.streetPlaceholder' })}
              value={deliveryAddress.street1}
              onChange={e => setDeliveryAddress(prev => ({ ...prev, street1: e.target.value }))}
              required
            />
            <div className={css.addressRow}>
              <input
                type="text"
                className={css.addressInput}
                placeholder={intl.formatMessage({ id: 'FulfillmentSelector.cityPlaceholder' })}
                value={deliveryAddress.city}
                onChange={e => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
                required
              />
              <input
                type="text"
                className={css.addressInput}
                placeholder={intl.formatMessage({ id: 'FulfillmentSelector.statePlaceholder' })}
                value={deliveryAddress.state}
                onChange={e => setDeliveryAddress(prev => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))}
                maxLength={2}
                required
              />
              <input
                type="text"
                className={css.addressInput}
                placeholder={intl.formatMessage({ id: 'FulfillmentSelector.zipPlaceholder' })}
                value={deliveryAddress.zip}
                onChange={e => setDeliveryAddress(prev => ({ ...prev, zip: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                maxLength={5}
                inputMode="numeric"
                pattern="[0-9]*"
                required
              />
            </div>
            <button
              type="submit"
              className={css.addressSubmit}
              disabled={
                !deliveryAddress.street1.trim() ||
                !deliveryAddress.city.trim() ||
                !deliveryAddress.state.trim() ||
                deliveryAddress.zip.length < 5 ||
                isLoadingQuote
              }
            >
              {isLoadingQuote
                ? intl.formatMessage({ id: 'FulfillmentSelector.loadingQuote' })
                : intl.formatMessage({ id: 'FulfillmentSelector.getQuoteButton' })}
            </button>
          </form>
        </div>
      )}

      {quoteError && (
        <p className={css.quoteError}>
          {intl.formatMessage({ id: 'FulfillmentSelector.quoteError' })}
        </p>
      )}
    </div>
  );
};

export default FulfillmentSelector;
